import React, { Fragment, useContext, useEffect, useState } from "react";
import { Button, ButtonGroup, Col, Input, Row, Table } from "reactstrap";
import _ from "lodash";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { api, constants, dateHelpers, helpers, UserContext } from "../utils";
import { ItemDropdownMenu, ServiceThreadSpinner, SpinnerButton } from './';
import { DesktopContainer } from "../layouts";
import { toast } from "react-toastify";
import classNames from "classnames";
import { useParams } from "react-router";

export default function SiteOrdersForItem(props) {
   const navigate = useNavigate();
   const userCtx = useContext(UserContext);
   const currentUser = userCtx.currentUser;
   const canEdit = currentUser?.isAPUCS;
   const { itemNum, siteId } = useParams();

   const [editCopy, setEditCopy] = useState(null);
   const [editIndex, setEditIndex] = useState(null);
   const [item, setItem] = useState({});
   const [loading, setLoading] = useState(false);
   const [orders, setOrders] = useState([]);
   const [working, setWorking] = useState(false);

   useEffect(() => {
      if (itemNum !== undefined && itemNum.length > 0) {
         setLoading(true);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [itemNum]);

   useEffect(() => {
      if (loading) {
         // escape the forward slash character or pound symbol if in the item so it won't mess up the URL
         // Example = MP.11/64S40007 this causes the url to look like it has an extra parameter because
         // of the / character so the URL is broken and a Not Found error is thrown.
         var encodedItem = itemNum.replace(/\//g, '%2F').replace(/#/g, '%23');

         api
            .post(`Order/GetItemSiteOrders/${encodedItem}/${siteId}`)
            .then(response => {
               if (response?.data?.success) {
                  setItem(response.data.item);
                  response?.data?.orders
                     ? setOrders(response.data.orders)
                     : setOrders([]);
               }
               else {
                  if (_.isString(response?.data?.message)) {
                     toast.error(response.data.message);
                  }
               }
            })
            .catch((error) => {
               api.catchHandler(error);
               toast.error(error);
            })
            .finally(() => {
               setLoading(false);
            });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [loading]);

   function allowEdit(index) {
      setEditIndex(index);
      setEditCopy(_.assign({}, orders[index]));
   }

   function cancelEdit() {
      setEditIndex(null);
      setEditCopy(null);
   }
   
   function getExcel() {
      setWorking(true);

      // escape the forward slash character or pound symbol if in the item so it won't mess up the URL
      // Example = MP.11/64S40007 this causes the url to look like it has an extra parameter because
      // of the / character so the URL is broken and a Not Found error is thrown.
      var encodedItem = itemNum.replace(/\//g, '%2F').replace(/#/g, '%23');
      api
         .postGetBlob(`Order/ExportItemSiteOrders/${encodedItem}/${siteId}`, null)
         .then((r) => {
            if (r.data) {
               const fileName = `${itemNum}_${siteId}_Orders_${dateHelpers.toMDYDateString(Date.now())}.xlsx`;
               helpers.downloadExport(r.data, fileName, constants.MIME_TYPES.xlsx);
            } else {
               toast.error(r.message || "Something went wrong exporting the data");
            }
         })
         .catch(api.catchHandler)
         .finally(() => {
            setWorking(false);
         });
   }

   function validateEditCopy() {
      if (editCopy === null) {
         return false;
      }
      if (!editCopy.shipDate) {
         return true;
      }
      const fulfilledAmt = editCopy.qtyApplied <= 0 ? 1 : editCopy.qtyApplied;
      if (editCopy.releaseQty < fulfilledAmt || editCopy.releaseQty > 9999999) {
         return true;
      }
      return false;
   }

   function handleChange(field, value) {
      const copyUpdate = _.assign({}, editCopy);
      copyUpdate[field] = value;
      setEditCopy(copyUpdate);
   }
   
   function updateOrderLine() {
      setWorking(true);
      const payload = _.chain({})
         .assign(editCopy)
         .value();

      if (_.keys(payload).length) {
         api
            .post(`Order/UpdateItemSiteOrder`, payload)
            .then((response) => {
               if (response.data.success) {
                  setLoading(true); // Reload the page
               } else {
                  if (_.isString(response?.data?.message)) {
                     toast.error(response.data.message);
                  }
               }
            })
            .catch(api.catchHandler)
            .finally(() => {
               cancelEdit();
               setWorking(false);
            });
      } else {
         cancelEdit();
         setWorking(false);
      }
   }


   return (
      <DesktopContainer screenName={"Site Orders for Item"}>
         <ServiceThreadSpinner on={loading}>
            {!_.isEmpty(item) && (
               <>
                  <Row
                     className={"mb-1 px-3"}
                     children={
                        <Table borderless>
                           <tbody>
                              <tr>
                                 <ItemDropdownMenu
                                    itemNumber={item.itemNumber}
                                    itemDescription={item.itemWithDesc}
                                    attachmentCount={item.itemAttachmentCount ?? 0}
                                 />
                              </tr>
                           </tbody>
                        </Table>
                     }
                  />
                  <Row className={"mb-1 px-2"}>
                     <Col xxl={6}
                        children={<span>Site: {item.siteWithDescription}</span>}
                     />
                  </Row>
                  <Row className={"mb-1 px-2"}>
                     <Col xxl={6}
                        children={<span>Item Class: {item.itemClass}</span>}
                     />
                  </Row>
                  <Row className={"mb-1 px-2"}>
                     <Col xxl={11}
                        children={
                           <span>
                              Allocated: {item.allocatedQty.toLocaleString("en-US", { maximumFractionDigits: 0, })}
                              &nbsp;&nbsp;&nbsp;&nbsp;
                              Back Ordered: {item.backOrderQty.toLocaleString("en-US", { maximumFractionDigits: 0, })}
                              &nbsp;&nbsp;&nbsp;&nbsp;
                              On Hand: {item.qtyOnHand.toLocaleString("en-US", { maximumFractionDigits: 0, })}
                              &nbsp;&nbsp;&nbsp;&nbsp;
                              Available: {item.qtyAvailable.toLocaleString("en-US", { maximumFractionDigits: 0, })}
                           </span>
                        }
                     />
                  </Row>
                  <br />
               </>
            )}


            {!_.isEmpty(orders) && (
               <>
                  <Row className={"mb-1 px-2"}>
                     <Col
                        xxl={10}
                        className="pt-3"
                        children={<span className="h5">Open Orders</span>}
                     />
                     <Col xxl={2}>
                        <SpinnerButton
                           title="Download as .xlsx"
                           className="shredButtonExcel float-end me-3"
                           onClick={() => getExcel()}
                           on={working}
                           disabled={working}
                        >
                           <FontAwesomeIcon icon="file-excel" size="lg" />
                        </SpinnerButton>
                     </Col>
                  </Row>
                  <Row
                     className={"mb-1 px-2"}
                     children={
                        <Table size="sm" className="data-table">
                           <thead className="table-header">
                              <tr>
                                 <th children={"Customer"} />
                                 <th style={{ width: "5rem" }} children={"Order #"} />
                                 <th children={<span>Order Date</span>} />
                                 <th children={<span>App<br />Qty</span>} />
                                 <th children={<span>BO<br />Qty</span>} />
                                 <th children={<span>Order<br />Qty</span>} />
                                 <th children={<span>Release<br />Qty</span>} />
                                 <th children={<span>Ship Date</span>} />
                                 {canEdit ? <th /> : null}
                              </tr>
                           </thead>
                           <tbody>
                              {_.map(orders, (x, index) => {
                                 const isEditing = editIndex === index;
                                 const readOnly = !isEditing;
                                 const shipDate = isEditing
                                    ? dateHelpers.formatDateForServer(editCopy.shipDate)
                                    : dateHelpers.toLongDateString(x.shipDate);

                                 return (
                                    <tr key={index} className={`tr-${index % 2 === 0 ? "primary" : "secondary"}`}>
                                       <td className="data-cell boldedOrange"
                                          style={{ cursor: "pointer" }}
                                          onClick={() => navigate(constants.ENDPOINTS.customerDashboard, { state: { label: x.customerWithName, value: x.customerId }, replace: true })}
                                       >{x.customerWithName}
                                       </td>
                                       <td className="data-cell">{x.orderId}</td>
                                       <td className="data-cell">{dateHelpers.toLongDateString(x.orderDate)}</td>
                                       <td className="data-cell">
                                          {x.qtyApplied.toLocaleString("en-US", {
                                             maximumFractionDigits: 0,
                                          })}
                                       </td>
                                       <td className="data-cell">
                                          {x.qtyBackOrdered.toLocaleString("en-US", {
                                             maximumFractionDigits: 0,
                                          })}
                                       </td>
                                       <td className="data-cell">
                                          {x.qtyOnOrder.toLocaleString("en-US", {
                                             maximumFractionDigits: 0,
                                          })}
                                       </td>
                                       <td className="data-cell">
                                          {readOnly ? (
                                             x.releaseQty.toLocaleString()
                                          ) : (
                                             <Input
                                                type="number"
                                                bsSize="sm"
                                                className={`px-1`}
                                                value={editCopy.releaseQty}
                                                onChange={(e) => handleChange("releaseQty", parseInt(e.target.value))}
                                             />
                                          )}
                                       </td>
                                       <td className="data-cell">
                                          {isEditing ? (
                                             <Fragment>
                                                <Row>
                                                   <Col>
                                                      <Input
                                                         type="date"
                                                         onChange={(e) => handleChange("shipDate", e.target.value)}
                                                         value={shipDate}
                                                         style={{ fontSize: "1em" }}
                                                      />
                                                   </Col>
                                                </Row>
                                             </Fragment>
                                          ) : (
                                                <span>{shipDate}</span>
                                          )}
                                       </td>
                                       {canEdit && (
                                          <td className="data-cell">
                                             <ButtonGroup className="width95px">
                                                <Button
                                                   className={`icon-button me-1 ${isEditing ? "" : "float-end"}`}
                                                   title={isEditing ? "Save changes" : "Edit line"}
                                                   color={isEditing ? "success" : "info"}
                                                   onClick={isEditing ? updateOrderLine : () => allowEdit(index)}
                                                   disabled={
                                                      (editIndex !== null && readOnly) ||
                                                      validateEditCopy() ||
                                                      working
                                                   }
                                                >
                                                   <FontAwesomeIcon
                                                      className="whiteIcon"
                                                      icon={isEditing ? "check" : "edit"}
                                                   />
                                                </Button>
                                                <Button
                                                   className={`icon-button ${isEditing ? "" : "vis-hidden"}`}
                                                   color="secondary"
                                                   title="Discard changes"
                                                   onClick={cancelEdit}
                                                   disabled={editIndex !== null && readOnly}
                                                >
                                                   <FontAwesomeIcon className="whiteIcon" icon="times" />
                                                </Button>
                                             </ButtonGroup>
                                          </td>
                                       )}
                                    </tr>
                                 );
                              })}
                           </tbody>
                           <tfoot className="table-footer">
                              <tr>
                                 <td colSpan="3" />
                                 <td className="fw-bold">{helpers.formatNumberWithThousands(_.sumBy(orders, o => o.qtyApplied))}<br /></td>
                                 <td className="fw-bold">{helpers.formatNumberWithThousands(_.sumBy(orders, o => o.qtyBackOrdered))}<br /></td>
                                 <td className="fw-bold">{helpers.formatNumberWithThousands(_.sumBy(orders, o => o.qtyOnOrder))}<br /></td>
                                 <td className="fw-bold">{helpers.formatNumberWithThousands(_.sumBy(orders, o => o.releaseQty))}<br /></td>
                                 <td />
                                 {canEdit ? <td /> : null}
                              </tr>
                           </tfoot>
                        </Table>
                     }
                  />
               </>
            )}
            {_.isEmpty(orders) && !_.isEmpty(item) ? (
               <Row className={classNames("px-5")}>
                  <Col
                     xxl={12}
                     className="pt-3"
                     children={
                        <span className="text-brightRed">
                           There are no open orders for this item and site
                        </span>
                     }
                  />
               </Row>
            ) : null}
         </ServiceThreadSpinner>
      </DesktopContainer>
   );
}
